import * as r from '@typoas/runtime';
import {
  mockGetVenue,
  mockShouldVerifyEmail,
  mockSignUp,
  mockVerifyCode,
} from './api-mock';
import { useMock } from './utils';
export enum BusinessSegment {
  HAIRDRESSER = 'hairdresser',
  BARBER = 'barber',
  NAILS = 'nails',
  AESTHETICS = 'aesthetics',
  SPA = 'spa',
  MASSAGE = 'massage',
  HAIR_REMOVAL = 'hair-removal',
  BEAUTICIAN = 'beautician',
}
export enum BusinessType {
  SHOP = 'shop',
  HOME = 'home',
  MOBILE = 'mobile',
  CHAIR_RENTAL = 'chair-rental',
  FREELANCER = 'freelancer',
}
export type CreateVenueParams = {
  businessName: string;
  businessType: BusinessType;
  businessSegments: BusinessSegment[];
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address: VenueAddress;
  dedicatedSpace: boolean;
  password: string;
  acceptsMarketing: boolean;
  verificationCode?: string | null;
  placeId?: string | null;
  leadId?: string | null;
  opportunityId?: string | null;
  gaSessionId?: string;
  gclid?: string;
  partnersURL?: string;
  adConsent?: number;
  token: string;
};
export type CreateVenueResponse = {
  errors: {
    type: ErrorType;
    key: string;
  }[];
  venueId?: number | null;
  supplierId?: number | null;
  communityAccountId?: number | null;
  extranetAccountId?: number | null;
};
export enum ErrorType {
  NOT_FOUND = 'NOT_FOUND',
  ID_MISMATCH = 'ID_MISMATCH',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  INVALID = 'INVALID',
}
export type GetVenueResponse = {
  redirectURL: string | null;
};
export type OpenApiModel = {
  /**
   * OpenAPI version
   * @example "3.0.3"
   */
  openapi: string;
  info?: {
    description?: string;
  };
  tags?: string[];
};
export type ShouldVerifyEmailResponse = {
  shouldVerifyEmail: boolean;
};
export type VenueAddress = {
  street: string;
  city: string;
  postalCode: string;
  countryCode: string;
};
export type VerifyCodeResponse = {
  success: boolean;
};
export type AuthMethods = {};
export function createContext<FetcherData>(
  params?: r.CreateContextParams<AuthMethods, FetcherData>,
): r.Context<AuthMethods, FetcherData> {
  return new r.Context<AuthMethods, FetcherData>({
    serverConfiguration: new r.ServerConfiguration('/ssi-api', {}),
    authMethods: {},
    ...params,
  });
}
/**
 * OpenAPI schema url
 * Tags: supervision
 */
export async function v1SupervisionOpenapiGenerateDocs<FetcherData>(
  ctx: r.Context<AuthMethods, FetcherData>,
  params: {},
  opts?: FetcherData,
): Promise<OpenApiModel> {
  const req = await ctx.createRequest({
    path: '/ssi-api/supervision/openapi/openapi.json',
    params,
    method: r.HttpMethod.GET,
  });
  const res = await ctx.sendRequest(req, opts);
  return ctx.handleResponse(res, {});
}
/**
 * OpenAPI ui
 * You can provide any schema file in query param
 * Tags: supervision
 */
export async function v1SupervisionOpenapiUi<FetcherData>(
  ctx: r.Context<AuthMethods, FetcherData>,
  params: {
    url?: string;
  },
  opts?: FetcherData,
): Promise<unknown> {
  const req = await ctx.createRequest({
    path: '/ssi-api/supervision/openapi/ui',
    params,
    method: r.HttpMethod.GET,
    queryParams: ['url'],
  });
  const res = await ctx.sendRequest(req, opts);
  return ctx.handleResponse(res, {});
}
/**
 * Calls the Java self-sign-up-api directly via the connect nginx-backend rewrite. The relative
 * path `/api/self-sign-up/venue/sign-up` resolves against the SPA's current origin (the connect
 * domain, where treatwell-nginx-backend rewrites `/api/self-sign-up/(.*)` to the internal Java
 * path `/self-sign-up-api/external-api/venue/sign-up`). Synchronous: a successful response
 * carries the new venue/supplier/account ids, a rejected one carries field-keyed errors. The
 * legacy TS-pod async job pattern (jobId + poll) is gone with CPG-787.
 *
 * Hand-rolled (not via the typoas client) because this endpoint targets a different host than
 * the 6 legacy `/ssi-api/*` endpoints in this file — those continue to use the typoas
 * `ServerConfiguration` base URL ({@link getAPIEndpoint}), this one needs a relative path.
 *
 * `partnersURL` rides in the request body (was a `lp_partners` query string on the TS proxy
 * which translated it into the body field server-side).
 */
export async function v1VenueCreateVenue(
  body: CreateVenueParams,
): Promise<CreateVenueResponse> {
  if (useMock) {
    return mockSignUp(body);
  }
  const res = await fetch('/api/self-sign-up/venue/sign-up', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`Sign-up request failed with status ${res.status}`);
  }
  return (await res.json()) as CreateVenueResponse;
}
/**
 * Tags: venue
 */
function connectCountryCodeToTLD(countryCode: string): string {
  return countryCode === 'GB' ? 'co.uk' : countryCode.toLowerCase();
}
/**
 * Calls the Java self-sign-up-api directly via the connect nginx-backend rewrite (same pattern as
 * {@link v1VenueCreateVenue}): the relative path `/api/self-sign-up/venue/existing-account-redirect`
 * is rewritten by treatwell-nginx-backend to the Java `/external-api/venue/existing-account-redirect`.
 *
 * The reCAPTCHA `token` rides in the body and is verified by the Java endpoint. Java returns the
 * existing Connect account's country code (or null); the Connect login URL is built here, as the TS
 * gateway used to do.
 *
 * Hand-rolled (not via the typoas client) because this targets a different host than the legacy
 * `/ssi-api/*` endpoints in this file.
 */
export async function v1VenueGetVenue(params: {
  email: string;
  token: string;
}): Promise<GetVenueResponse> {
  if (useMock) {
    return mockGetVenue(params.email);
  }
  const res = await fetch('/api/self-sign-up/venue/existing-account-redirect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    throw new Error(`Existing-account lookup failed with status ${res.status}`);
  }
  const body = (await res.json()) as { connectCountryCode: string | null };
  return {
    redirectURL: body.connectCountryCode
      ? `https://connect.treatwell.${connectCountryCodeToTLD(
          body.connectCountryCode,
        )}`
      : null,
  };
}
/**
 * Calls the Java self-sign-up-api directly via the connect nginx-backend rewrite (same pattern as
 * {@link v1VenueGetVenue}): the relative path `/api/self-sign-up/venue/account/active` is rewritten
 * by treatwell-nginx-backend to the Java `/external-api/venue/account/active`.
 *
 * The reCAPTCHA `token` rides in the body and is verified by the Java endpoint. Java answers the
 * domain-level question (`hasActiveAccount`); we translate it to `shouldVerifyEmail` here, as the TS
 * gateway used to do.
 *
 * Hand-rolled (not via the typoas client) because this targets a different host than the legacy
 * `/ssi-api/*` endpoints in this file.
 */
export async function v1VenueShouldVerifyEmail(params: {
  email: string;
  token: string;
}): Promise<ShouldVerifyEmailResponse> {
  if (useMock) {
    return mockShouldVerifyEmail(params.email);
  }
  const res = await fetch('/api/self-sign-up/venue/account/active', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    throw new Error(`Active-account lookup failed with status ${res.status}`);
  }
  const body = (await res.json()) as { hasActiveAccount: boolean };
  return { shouldVerifyEmail: body.hasActiveAccount };
}
export type SendVerificationCodeOutcome = 'SENT' | 'RATE_LIMITED';
export type SendVerificationCodeParams = {
  email: string;
  token: string;
  firstName: string;
  lastName: string;
  businessName: string;
  language: string;
};
/**
 * Calls the Java self-sign-up-api directly via the connect nginx-backend rewrite (same pattern as
 * {@link v1VenueCreateVenue}): the relative path `/api/self-sign-up/venue/verification/send`
 * resolves against the SPA origin, where treatwell-nginx-backend rewrites `/api/self-sign-up/(.*)`
 * to the internal Java path `/self-sign-up-api/external-api/venue/verification/send`.
 *
 * The reCAPTCHA `token` rides in the body and is verified by the Java endpoint (it is no longer
 * checked by the TS gateway). A 401 means the token was rejected and surfaces as a thrown error.
 * A 429 (per-email send limit) is expected and reported as `RATE_LIMITED` rather than thrown.
 *
 * Hand-rolled (not via the typoas client) because this targets a different host than the legacy
 * `/ssi-api/*` endpoints in this file.
 */
export async function v1VerificationSendVerificationCode(
  params: SendVerificationCodeParams,
): Promise<SendVerificationCodeOutcome> {
  if (useMock) {
    return 'SENT';
  }
  const res = await fetch('/api/self-sign-up/venue/verification/send', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (res.status === 429) {
    return 'RATE_LIMITED';
  }
  if (!res.ok) {
    throw new Error(
      `Send verification code request failed with status ${res.status}`,
    );
  }
  return 'SENT';
}
/**
 * Calls the Java self-sign-up-api directly via the connect nginx-backend rewrite (same pattern as
 * {@link v1VenueCreateVenue}): the relative path `/api/self-sign-up/venue/verification/check` is
 * rewritten by treatwell-nginx-backend to the Java `/external-api/venue/verification/check`.
 *
 * The reCAPTCHA `token` rides in the body and is verified by the Java endpoint. This is the
 * non-consuming check used for pre-submit feedback; the consuming verify still happens server-side
 * at signup completion.
 *
 * Hand-rolled (not via the typoas client) because this targets a different host than the legacy
 * `/ssi-api/*` endpoints in this file.
 */
export async function v1VerificationVerifyCode(params: {
  email: string;
  code: string;
  token: string;
}): Promise<VerifyCodeResponse> {
  if (useMock) {
    return mockVerifyCode(params.code);
  }
  const res = await fetch('/api/self-sign-up/venue/verification/check', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    throw new Error(`Verify code request failed with status ${res.status}`);
  }
  return (await res.json()) as VerifyCodeResponse;
}
